
import { useRoute, useRouter } from "vue-router";
import { computed, defineComponent, inject, ref } from "vue";
import { useSidClient } from "@/plugins/auth";
import { BaseInfo, ShippingInfo, SiteInfo, UserBase, UserRimacomi } from "@/commons/interfaces";
import { MEMBER_AGREE_INFO_VERSION } from "@/commons/constants";
import moment from "moment";
import useDeterring from "@/commons/useDeterring";
import { LinkageItem } from "@/modules/linkage/linkageInfo";
import { isAuthFailed, isSiteRegistered } from "@/commons/commonUtility";
import { injectSiteUniqItem, injectUserBaseItem } from "@/commons/linkage/linkageCommon";
import { ImageItem, ImageItemKey } from "@/modules/imageInfo";
import { arrayBufferToBase64, readFile } from "@/commons/imageFormatUtility";

export default defineComponent({
    name: "RimacomiConfirm",
    props: {
        // 戻るボタン押下時の遷移先
        prevRoute: { type: String, required: true },
        // 登録・更新ボタン押下時の遷移先(nullの場合はトークンコールバックする)
        nextRoute: { type: String },
    },
    setup(props) {
        const sidClient = useSidClient();
        const router = useRouter();
        const route = useRoute();
        const { isDeterringRef, setNotRelease, asyncDeterringWrap } = useDeterring();
        const { linkageInfo, fetchToken, routeNames, siteUniqApiPath } = LinkageItem.inject();

        const userBaseItem = injectUserBaseItem(linkageInfo.linkageId);
        const siteUniqItem = injectSiteUniqItem(linkageInfo.linkageId);

        const userBase = userBaseItem.userData as UserBase;
        const userRimacomi = siteUniqItem.userData as UserRimacomi;
        const errorMsgRef = ref("");
        const isDispErrorMsgRef = ref(false);
        const nickName = computed(() => userBase.commonAttrInfo?.nickName?.replaceAll("&lt;", "<").replaceAll("&gt;", ">") || "");
        const { image } = inject(ImageItemKey) as ImageItem;
        const date = computed(() => {
            const m = moment(userBase.commonAttrInfo.birthday);
            return { year: m.year(), month: m.month() + 1, day: m.date() };
        });
        // 初回サービス登録判定
        const firstServiceReg = route.name === `${routeNames.LOGIN_PROFILE_CONFIRM}Child` && !isSiteRegistered(linkageInfo.linkageId, userBase);
        // 会員移行後初回ログイン判定（基本属性情報：存在する+siteInfoにリマコミ＋の登録が無い、サイト固有情報：存在する）
        const firstLoginAfterMgn = userBase.identityId && userRimacomi.identityId && !isSiteRegistered(linkageInfo.linkageId, userBase);

        /**
         * 送付先情報設定
         * @param baseInfo 登録者情報
         * return shippingInfo
         */
        const setShippingInfo = (baseInfo: BaseInfo) => {
            const shippingInfo = new ShippingInfo();
            shippingInfo.lastName = baseInfo.lastName;
            shippingInfo.firstName = baseInfo.firstName;
            shippingInfo.lastKana = baseInfo.lastKana;
            shippingInfo.firstKana = baseInfo.firstKana;
            shippingInfo.zipCode = baseInfo.zipCode;
            shippingInfo.prefecture = baseInfo.prefecture;
            shippingInfo.address = baseInfo.address;
            shippingInfo.building = baseInfo.building;
            shippingInfo.tel = baseInfo.tel;
            return shippingInfo;
        };

        /**
         * プロフィール画像更新
         * @async
         * @param file プロフィール画像
         */
        const imageUpdate = async (file: Blob) => {
            if (userBase.commonAttrInfo.imagePath) {
                if (!image.path) {
                    // 以前にプロフィール画像を登録していて、削除する場合
                    // プロフィール画像削除
                    await sidClient.requestApi({
                        method: "DELETE",
                        path: "/user/me/image",
                    });
                    return;                    
                } else {
                    if (userBase.commonAttrInfo.imagePath !== image.path) {
                        // 以前にプロフィール画像を登録していて、以前と違う画像を登録する場合
                        // プロフィール画像更新
                        const binaryData = await readFile(file);
                        const base64Data: any = arrayBufferToBase64(binaryData);                                     
                        await sidClient.requestApi({
                            method: "PUT",
                            path: "/user/me/image",
                            body: base64Data
                        });
                        return;
                    }
                    // 以前にプロフィール画像が登録していて、変更しない場合
                    // 特に何もしない
                }
            } else {
                if (image.path) {
                    // 以前にプロフィール画像を登録しておらず、今回登録する場合
                    // プロフィール画像登録
                    const binaryData = await readFile(file);
                    const base64Data: any = arrayBufferToBase64(binaryData);               
                    await sidClient.requestApi({
                        method: "POST",
                        path: "/user/me/image",
                        body: base64Data
                    });
                    return;
                }
                // 以前にプロフィール画像を登録しておらず、今回も登録しない場合
                // 特に何もしない
            }
        };

        const upsertUserBase = async () => {

            const today = moment(new Date()).format("YYYY-MM-DD");

            // 送付先情報設定
            userBase.shippingInfo[0] = setShippingInfo(userBase.baseInfo);

            // 連携情報登録有無を確認
            const registeredSite = userBase.siteInfo.find(site => site.enable && site.siteLinkageId === linkageInfo.linkageId);
            if (!registeredSite) {
                // 対象サイト以外のリスト
                const newSites = userBase.siteInfo.filter(site => site.siteLinkageId !== linkageInfo.linkageId);
                // 対象サイトの情報の登録情報を作成
                const addSite: SiteInfo = {
                    siteLinkageId: linkageInfo.linkageId,
                    enable: true,
                    regDate: moment(new Date()).format("YYYY-MM-DD"),
                    leaveDate: null
                };
                newSites.push(addSite);
                userBase.siteInfo = newSites;
            }

            // 会員規約合意情報の設定
            userBase.memberAgreeInfo.version = MEMBER_AGREE_INFO_VERSION;
            userBase.memberAgreeInfo.regDate = today;

            if (!userBase.identityId) {
                // 新規登録
                // 要求用にオブジェクトをコピー
                const reqUserInfo = Object.assign({}, userBase);
                // ニックネームをエスケープ
                reqUserInfo.commonAttrInfo.nickName = reqUserInfo?.commonAttrInfo?.nickName?.replaceAll("<", "&lt;").replaceAll(">", "&gt;") || "";

                const payload = JSON.parse(JSON.stringify(reqUserInfo));
                await sidClient.createUserAttr(payload);
            } else {
                // 更新
                // 要求用にオブジェクトをコピー
                const reqUserInfo = {
                    identityId: userBase.identityId,
                    manageInfo: userBase.manageInfo,
                    commonAttrInfo: {
                        gender: userBase.commonAttrInfo.gender,
                        birthday: userBase.commonAttrInfo.birthday ?? null,
                        // ニックネームをエスケープ
                        nickName: userBase?.commonAttrInfo?.nickName?.replaceAll("<", "&lt;").replaceAll(">", "&gt;") || "",
                    },
                    siteInfo: userBase.siteInfo
                };

                const payload = JSON.parse(JSON.stringify(reqUserInfo));
                await sidClient.updateUserAttr(payload);
            }
        };

        const upsertSiteUniq = async () => {

            // 要求用にオブジェクトをコピー
            const reqRimacomiInfo = Object.assign({}, userRimacomi);

            // サイト固有情報登録or更新
            if (!userRimacomi.identityId) {
                await sidClient.requestApi({
                    method: "POST",
                    path: siteUniqApiPath,
                    body: reqRimacomiInfo
                });
            } else {
                await sidClient.requestApi({
                    method: "PUT",
                    path: siteUniqApiPath,
                    body: reqRimacomiInfo
                });
            }
        };

        const upsert = async () => {

            try {
                // 基本属性情報、サイト固有情報登録(パラレル)
                const results = await Promise.allSettled([
                    upsertUserBase(),
                    upsertSiteUniq()
                ]);
                const rejected = results.find(res => res.status === "rejected") as PromiseRejectedResult | undefined;
                if (rejected) {
                    throw rejected.reason;
                }
                // プロフィール画像更新
                await imageUpdate(image.file);
                userBaseItem.removeStorage();
                siteUniqItem.removeStorage();
            } catch (e) {
                if (isAuthFailed(e)) {
                    if (linkageInfo.email) {
                        if (route.name === routeNames.SIGNUP_CONFIRM) {
                            // 本人確認画面へ遷移
                            await router.push({ name: routeNames.SIGNUP_IDENTIFICATION, params: { message: "認証の有効期限が切れました。再ログイン後にもう一度最初から操作をお願いします。" } });
                        } else {
                            // 本人確認画面へ遷移
                            await router.push({ name: routeNames.IDENTIFICATION, query: { retUrl: routeNames.PROFILE }, params: { message: "認証の有効期限が切れました。再ログイン後にもう一度最初から操作をお願いします。" } });
                        }
                    } else {
                        // ログイン画面へ遷移
                        await router.push({ name: routeNames.LOGIN, params: { message: "認証の有効期限が切れました。再ログイン後にもう一度最初から操作をお願いします。" } });
                    }
                    return;
                }
                // 前画面に戻る
                await Promise.allSettled([userBaseItem.fetchUserData(sidClient), siteUniqItem.fetchUserData(sidClient)])
                    .finally(async () => {
                        userBaseItem.userData.userName = siteUniqItem.userData.userName = (await sidClient.getUserData()).Username;
                        if (e.response.data === "rimacomiId is invalid") {
                            router.push({ name: props.prevRoute, params: { message: "ご入力のリマコミ＋IDはご利用できません。別のリマコミ＋IDでお試しください。" } });
                        } else {
                            router.push({ name: props.prevRoute, params: { message: "会員情報の更新に失敗しました。再度お試し下さい。" } });
                        }
                    });
                return;
            }

            if (props.nextRoute) {
                // 完了画面へ遷移
                await router.push({ name: props.nextRoute });
            } else {
                try {
                    await fetchToken(sidClient);
                    setNotRelease();
                    window.location.href = linkageInfo.callbackUrlWithToken as string;
                } catch (e) {
                    await router.push({ name: routeNames.GENERAL_ERROR, params: { message: "お手数をおかけしますが、しばらく時間を置いてから、再度操作していただけますようお願いいたします。" } });
                }
            }
        };

        return {
            isDeterringRef,
            asyncDeterringWrap,
            userBase,
            userRimacomi,
            date,
            nickName,
            image,
            errorMsgRef,
            isDispErrorMsgRef,
            upsert,
            linkageInfo,
            firstServiceReg,
            firstLoginAfterMgn,
            routeNames,
            router
        };
    },
});
